/**
 * Código TOTP generado por el servidor (la semilla nunca sale) con el anillo de
 * cuenta regresiva del mockup. Cada generación queda auditada (`totp_read`), por
 * eso se refresca solo unas pocas veces y después pide un click.
 */
import { useEffect, useState } from 'react';
import { Copy, RefreshCw } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { Button } from '@/components/ui/button';
import { errMsg, useCopy, useVaultMutations, withUnlockRetry } from '@/hooks/useVault';
import { useCountdown } from '@/hooks/useCountdown';

const AUTO_REFRESHES = 3;

interface TotpFieldProps {
  secretId: string;
  digits: number;
  step: number;
  ensureUnlocked: () => Promise<boolean>;
  /** Genera el primer código al montar (el asistente de MFA lo muestra para comparar con el teléfono). */
  autoStart?: boolean;
}

export function TotpField({ secretId, digits, step, ensureUnlocked, autoStart }: TotpFieldProps) {
  const { t } = useTranslation();
  const copy = useCopy();
  const { getTotp } = useVaultMutations();
  const [code, setCode] = useState<string | null>(null);
  const [expiresIn, setExpiresIn] = useState(0);
  const [refreshes, setRefreshes] = useState(0);
  const [error, setError] = useState<string | null>(null);

  const fetchCode = async () => {
    setError(null);
    try {
      const res = await withUnlockRetry(ensureUnlocked, () => getTotp.mutateAsync({ secretId }));
      if (!res) return;
      setCode(res.code);
      setExpiresIn(res.expiresIn);
    } catch (e) {
      setError(errMsg(e as { message?: string }));
    }
  };

  // al vencer se refresca solo unas pocas veces (cada lectura queda auditada) y después pide un clic
  const left = useCountdown(code === null ? null : expiresIn, code, () => {
    if (refreshes < AUTO_REFRESHES) {
      setRefreshes((n) => n + 1);
      void fetchCode();
    } else {
      setCode(null);
    }
  });

  useEffect(() => {
    if (autoStart) void fetchCode();
    // `fetchCode` se recrea en cada render (usa la mutation); solo importa arrancar una vez por credencial
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [autoStart, secretId]);

  const pretty = code ? `${code.slice(0, Math.ceil(digits / 2))} ${code.slice(Math.ceil(digits / 2))}` : '—'.repeat(3);
  const progress = code ? left / step : 0;

  return (
    <div className="flex items-center gap-3">
      <span className="font-mono text-2xl tracking-widest text-brand-green" data-tour={code ? 'totp-code' : undefined}>{pretty}</span>
      {code && (
        <span className="relative h-7 w-7 shrink-0" title={t('vault.totp.expires', 'expira en {{s}} s', { s: left })}>
          <svg viewBox="0 0 36 36" className="h-7 w-7 -rotate-90">
            <circle cx="18" cy="18" r="15" fill="none" stroke="currentColor" strokeWidth="3" className="text-muted" />
            <circle
              cx="18" cy="18" r="15" fill="none" stroke="#1CC9B9" strokeWidth="3"
              strokeDasharray={`${Math.max(progress, 0) * 94.2} 94.2`}
            />
          </svg>
        </span>
      )}
      {code && <span className="text-xs text-muted-foreground">{t('vault.totp.expires', 'expira en {{s}} s', { s: left })}</span>}
      {code === null ? (
        <Button variant="outline" size="sm" onClick={() => { setRefreshes(0); void fetchCode(); }} disabled={getTotp.isPending} data-tour="totp-generate">
          <RefreshCw className={`mr-2 h-3.5 w-3.5 ${getTotp.isPending ? 'animate-spin' : ''}`} />
          {t('vault.totp.generate', 'Generar código')}
        </Button>
      ) : (
        <Button variant="outline" size="icon" onClick={() => copy(code, t('vault.totp.copied', 'Código copiado'))} title={t('vault.reveal.copy', 'Copiar')} aria-label={t('vault.reveal.copy', 'Copiar')} data-tour="totp-copy">
          <Copy className="h-4 w-4" />
        </Button>
      )}
      {error && <span className="text-xs text-destructive">{error}</span>}
    </div>
  );
}
